import "./style.css";
import pkg from "../package.json";
import type { Object3D } from "three";
import { sound } from "./audio/sound";
import { chooseAiMove, type AiDifficulty } from "./game/ai";
import { getAssistantAdvice } from "./game/assistant";
import { GameClock, resolveTimeControl } from "./game/clock";
import { ChessEngine, type PromotionPiece } from "./game/engine";
import { isInsufficientMaterial } from "./game/material";
import { captureSquaresOf } from "./game/moves";
import { TaverneuDirector } from "./game/taverneu";
import { TUTORIAL_STEPS } from "./game/tutorialScript";
import { STRATEGY_TUTORIAL_STEPS } from "./game/strategyTutorialScript";
import { STRATEGY_EXERCISES } from "./game/exercises";
import type { Color, HistoryEntry, Piece } from "./game/types";
import { SceneManager } from "./render/scene";
import { PieceLayer } from "./render/pieceEntities";
import { MoveMarkers } from "./render/highlights";
import { showMenu, type MenuResult } from "./ui/menu";
import { showOnlineMenu, tryResumeOnlineSession, type OnlineMatch } from "./ui/onlineMenu";
import {
  clearStoredSession,
  type ChessRemoteState,
  type GameOverPayload,
  type PlayerDisconnectedPayload,
  type PlayerReconnectedPayload,
} from "./net/onlineClient";
import { loadMenuSettings, saveMenuSettings } from "./ui/menuSettings";
import { loadGraphicsQuality } from "./ui/graphicsSettings";
import { Hud } from "./ui/hud";
import { TaverneuBubble } from "./ui/taverneuBubble";
import { TutorialController } from "./ui/tutorialController";
import { ExerciseController } from "./ui/exerciseController";
import { showPromotionModal } from "./ui/promotionModal";
import { showEndModal } from "./ui/endModal";
import { showRulesModal } from "./ui/rulesModal";
import { showConfirmModal } from "./ui/confirmModal";
import { showOptionsModal } from "./ui/optionsModal";
import { WakeLockController } from "./ui/wakeLock";

const delay = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/** Rideau noir plein ecran pour la transition menu -> intro de partie (memes fondus que dkg-farkle). */
class SceneFade {
  private readonly el: HTMLDivElement;

  constructor() {
    this.el = document.createElement("div");
    this.el.id = "scene-fade";
    document.body.appendChild(this.el);
  }

  async toBlack(durationMs = 380): Promise<void> {
    this.el.style.setProperty("--fade-duration", `${durationMs}ms`);
    this.el.classList.add("visible");
    await delay(durationMs);
  }

  async fromBlack(durationMs = 700): Promise<void> {
    this.el.style.setProperty("--fade-duration", `${durationMs}ms`);
    this.el.classList.remove("visible");
    await delay(durationMs);
  }
}

/** Libelle francais d'une nulle, partage par le mode local et le mode en ligne. */
function describeDraw(status: string): string {
  switch (status) {
    case "draw-repetition":
      return "Nulle par triple repetition";
    case "draw-fifty-moves":
      return "Nulle par la regle des 50 coups";
    case "draw-insufficient-material":
      return "Nulle par materiel insuffisant";
    default:
      return "Partie nulle";
  }
}

async function main(): Promise<void> {
  const versionTag = document.createElement("div");
  versionTag.id = "version-tag";
  versionTag.textContent = `v${pkg.version}`;
  document.body.appendChild(versionTag);

  // Empeche l'ecran de s'eteindre tant que la page reste ouverte (menu compris),
  // pas seulement pendant une partie.
  void new WakeLockController().acquire();

  const sceneFade = new SceneFade();
  const viewport = document.getElementById("viewport");
  if (!viewport) throw new Error("#viewport introuvable");
  const uiRoot = document.getElementById("ui-root");
  if (!uiRoot) throw new Error("#ui-root introuvable");

  // Une session en ligne interrompue (refresh, coupure reseau) est reprise avant meme
  // d'afficher le menu : le serveur garde le joueur 3 min avant de le declarer forfait.
  const resumed = await tryResumeOnlineSession();
  if (resumed) {
    await mainOnline(resumed, sceneFade, viewport, uiRoot);
    return;
  }

  // Boucle menu <-> tutoriel <-> salon en ligne : ces entrees ne resolvent pas la promesse
  // de showMenu (elle reste en attente, abandonnee), on relance simplement un tour de boucle
  // une fois l'ecran quitte, jusqu'a ce que "Commencer la partie" fournisse un vrai resultat
  // (ou que le salon en ligne apparie une partie).
  let result: MenuResult | undefined;
  let onlineMatch: OnlineMatch | undefined;
  while (!result && !onlineMatch) {
    const stored = loadMenuSettings();
    result = await new Promise<MenuResult | undefined>((resolve) => {
      void showMenu(
        stored,
        (kind, difficulty) => {
          if (kind === "exercises") {
            const exercises = STRATEGY_EXERCISES.filter((e) => e.difficulty === difficulty);
            new ExerciseController(uiRoot, viewport, exercises, () => resolve(undefined));
            return;
          }
          const steps =
            kind === "rules"
              ? TUTORIAL_STEPS
              : STRATEGY_TUTORIAL_STEPS.filter((s) => !s.difficulty || s.difficulty === difficulty);
          new TutorialController(uiRoot, viewport, steps, () => resolve(undefined));
        },
        () => {
          void showOnlineMenu().then((match) => {
            if (match) onlineMatch = match;
            resolve(undefined);
          });
        },
      ).then((menuResult) => resolve(menuResult));
    });
  }
  if (onlineMatch) {
    await mainOnline(onlineMatch, sceneFade, viewport, uiRoot);
    return;
  }
  if (!result) return;
  const { white, black, timeControlId } = result;
  // Fusionne sur les reglages actuels (plutot que reconstruire l'objet a la main) pour ne
  // pas ecraser showMoveHints/taverneuCommentary avec des valeurs par defaut : ces deux
  // champs ne transitent pas par MenuResult, ils sont modifies en direct (modale Options,
  // accessible aussi bien depuis le menu que depuis le HUD en jeu) et deja a jour en stockage.
  const settings = { ...loadMenuSettings(), white, black, timeControlId };
  saveMenuSettings(settings);
  const timeControl = resolveTimeControl(timeControlId);
  const names: Record<Color, string> = { w: white.name, b: black.name };
  const isAI: Record<Color, boolean> = { w: white.isAI, b: black.isAI };
  const difficulty: Record<Color, AiDifficulty> = { w: white.difficulty, b: black.difficulty };
  // La camera ne pivote automatiquement que s'il y a exactement 2 humains (hot-seat,
  // l'ecran change de mains) ou 0 (partie bot contre bot, spectateur). Avec un seul
  // humain, elle reste fixe de son cote une bonne fois pour toutes.
  const humanCount = (white.isAI ? 0 : 1) + (black.isAI ? 0 : 1);
  const soleHumanColor: Color | null = humanCount === 1 ? (white.isAI ? "b" : "w") : null;
  const pivotEnabled = soleHumanColor === null;
  // Le Taverneu commente du point de vue de l'humain seul s'il y en a un, sinon des
  // Blancs par defaut (hot-seat ou partie bot contre bot en spectateur).
  const taverneuDirector = new TaverneuDirector(soleHumanColor ?? "w");

  await sceneFade.toBlack();
  document.querySelectorAll(".menu-screen").forEach((el) => el.remove());

  const scene = new SceneManager(viewport, loadGraphicsQuality());
  const canvas = scene.renderer.domElement;

  const engine = new ChessEngine();
  const pieceLayer = new PieceLayer(scene.piecesGroup, scene.tweens);
  const markers = new MoveMarkers(scene.piecesGroup);

  if (soleHumanColor !== null) {
    scene.setBaseAzimuth(soleHumanColor);
  }

  if (import.meta.env.DEV) {
    (window as unknown as { __debug: unknown }).__debug = { scene, engine, pieceLayer };
  }

  const captured: Record<Color, Piece[]> = { w: [], b: [] };

  let selected: number | null = null;
  // Reste vrai le temps de l'intro (pieces qui tombent en cascade derriere le rideau) :
  // pas d'interaction possible avant que la scene soit entierement posee et revelee.
  let busy = true;
  let gameOver = false;
  // Declare avant le premier `void maybeTriggerAiMove()` plus bas : en partie bot contre
  // bot, ce premier appel peut atteindre `setHover` (qui lit cette variable) de maniere
  // synchrone, avant que l'ancienne position (apres les fonctions utilitaires) ne soit
  // executee.
  let hoveredSquare: number | null = null;
  // Modifiables en direct depuis la modale Options (bouton du sandwich en jeu), sans
  // attendre un rechargement — cf. `onOptions` ci-dessous.
  let showMoveHints = settings.showMoveHints;
  let taverneuCommentary = settings.taverneuCommentary;

  const hud = new Hud(names, {
    onRules: () => showRulesModal(),
    onOptions: () =>
      showOptionsModal({
        onMoveHintsChange: (enabled) => {
          showMoveHints = enabled;
        },
        onTaverneuCommentaryChange: (enabled) => {
          taverneuCommentary = enabled;
          if (!enabled) taverneuBubble.hide();
        },
        onGraphicsQualityChange: (quality) => scene.setQuality(quality),
      }),
    onResign: async () => {
      if (gameOver) return;
      const ok = await showConfirmModal("Voulez-vous vraiment abandonner la partie ?", "Abandonner");
      if (!ok || gameOver) return;
      const winner = engine.turn === "w" ? "b" : "w";
      endGame(`${names[winner]} gagne`, `${names[engine.turn]} abandonne`);
    },
    onAssistant: () => {
      // Reserve au trait humain : pendant le calcul de l'IA ou une animation, il n'y a rien
      // de pertinent a suggerer (et `selected` ne doit pas etre pose sur un coup adverse).
      if (busy || gameOver || isAI[engine.turn]) return;
      const advice = getAssistantAdvice(engine.getState());
      if (advice.move) {
        // Pose la piece suggeree en "selectionnee" : cliquer la case surlignee joue directement
        // le coup, via le meme chemin que n'importe quel coup choisi a la main.
        selected = advice.move.from;
        markers.showSelection(advice.move.from);
        const isCapture = !!advice.move.captured || advice.move.enPassantCapture !== undefined;
        markers.showTargets([advice.move.to], isCapture ? new Set([advice.move.to]) : undefined);
      }
      taverneuBubble.say({ text: advice.text });
    },
  });
  hud.setActive("w");
  const taverneuBubble = new TaverneuBubble(uiRoot);

  // `null` = partie sans chrono (comportement historique). Le camp au trait charge son
  // temps y compris pendant que l'IA "reflechit" : c'est le comportement d'un vrai
  // chrono d'echecs, pas une lacune.
  const clock = timeControl
    ? new GameClock(timeControl, {
        onTick: (remaining) => {
          hud.updateClock("w", remaining.w);
          hud.updateClock("b", remaining.b);
        },
        onFlag: (color) => {
          if (gameOver) return;
          if (isInsufficientMaterial(engine.getState().board)) {
            endGame("Nulle", "Drapeau tombe, mais materiel insuffisant pour mater");
            return;
          }
          const winner = color === "w" ? "b" : "w";
          endGame(`${names[winner]} gagne`, `${names[color]} a depasse le temps imparti`);
        },
      })
    : null;
  if (clock) {
    hud.showClock();
    clock.startTicking();
  }

  // La scene (torches et chandelles deja vacillantes, pieces deja en train de tomber,
  // camera deja plongeant vers le plateau, HUD deja en place) est ainsi "vivante" des
  // que le rideau se leve, plutot que figee.
  const introPromise = pieceLayer.playIntro(engine.getState().board);
  const swoopPromise = scene.playIntroSwoop();
  await sceneFade.fromBlack();
  await Promise.all([introPromise, swoopPromise]);
  clock?.resume(engine.turn);
  busy = false;
  if (taverneuCommentary) taverneuBubble.say(taverneuDirector.greet());
  void maybeTriggerAiMove();

  function clearSelection(): void {
    selected = null;
    markers.hideAll();
  }

  /** Remonte la hierarchie jusqu'au Group racine d'une piece (seul niveau qui porte `userData.square`). */
  function squareOfIntersected(object: Object3D): number | undefined {
    let node: Object3D | null = object;
    while (node) {
      if (typeof node.userData.square === "number") return node.userData.square;
      node = node.parent;
    }
    return undefined;
  }

  function setHover(square: number | null): void {
    if (square === hoveredSquare) return;
    hoveredSquare = square;
    markers.showHover(square);
    canvas.style.cursor = square !== null ? "pointer" : "";
  }

  /** Survol des pieces jouables : uniquement les siennes, uniquement quand c'est a l'humain de jouer. */
  function updateHover(clientX: number, clientY: number): void {
    if (busy || gameOver || isAI[engine.turn]) {
      setHover(null);
      return;
    }
    const intersects = scene.raycastFromPointer(clientX, clientY, [scene.piecesGroup]);
    const hit = intersects[0];
    const square = hit ? squareOfIntersected(hit.object) : undefined;
    if (square === undefined) {
      setHover(null);
      return;
    }
    const piece = engine.getState().board[square];
    setHover(piece && piece.color === engine.turn ? square : null);
  }

  function selectSquare(square: number): void {
    selected = square;
    const moves = engine.legalMovesFrom(square);
    markers.showSelection(square);
    const targets = showMoveHints ? moves.map((m) => m.to) : [];
    markers.showTargets(targets, captureSquaresOf(moves));
  }

  function endGame(title: string, subtitle: string): void {
    gameOver = true;
    clock?.dispose();
    clearSelection();
    setHover(null);
    showEndModal(title, subtitle, () => window.location.reload());
  }

  async function afterMove(entry: HistoryEntry): Promise<void> {
    clock?.pause();
    clock?.addIncrement(entry.stateBefore.turn);
    sound.pieceMove();
    hud.addHistoryEntry(entry.stateBefore.fullmoveNumber, entry.stateBefore.turn, entry.san);

    if (entry.move.captured) {
      const by = entry.move.piece.color;
      captured[by].push(entry.move.captured);
      hud.setCaptured(by, captured[by]);
    }

    const status = engine.getStatus();
    const taverneuLine = taverneuDirector.onMove(entry, status);
    if (taverneuLine && taverneuCommentary) taverneuBubble.say(taverneuLine);

    if (status === "checkmate") {
      const winner = entry.stateBefore.turn;
      endGame("Echec et mat", `${names[winner]} remporte la partie`);
      return;
    }
    if (status === "stalemate") {
      endGame("Pat", "Partie nulle : aucun coup legal");
      return;
    }
    if (status.startsWith("draw-")) {
      endGame("Nulle", describeDraw(status));
      return;
    }

    clock?.resume(engine.turn);
    if (pivotEnabled) {
      await scene.pivotToPlayer(engine.turn);
    }
    hud.setActive(engine.turn);
    hud.showTurnAnnounce(names[engine.turn], status === "check" ? "Echec !" : undefined);

    void maybeTriggerAiMove();
  }

  /** Si la case au trait est controlee par un bot, calcule et joue son coup (meme pipeline que pour un coup humain). */
  async function maybeTriggerAiMove(): Promise<void> {
    if (gameOver || !isAI[engine.turn]) return;
    const turn = engine.turn;
    busy = true;
    setHover(null);
    hud.setThinking(turn, true);
    // Petite pause avant de reflechir : laisse le pivot/l'annonce de tour se lire,
    // et laisse le navigateur peindre l'etat "reflechit" avant le calcul (bloquant, cf. src/game/ai.ts).
    await delay(500);
    const move = chooseAiMove(engine.getState(), difficulty[turn]);
    // Le calcul ci-dessus est synchrone et peut bloquer plusieurs secondes (IA "difficile") :
    // le setInterval du chrono n'a pas pu s'executer pendant ce temps. On rattrape ici un
    // drapeau tombe pendant la reflexion, avant de jouer un coup qui serait deja hors delai.
    clock?.tick();
    if (gameOver) {
      busy = false;
      return;
    }
    hud.setThinking(turn, false);
    if (!move) {
      busy = false;
      return;
    }
    const entry = engine.move(move.from, move.to, move.promotion);
    if (!entry) {
      busy = false;
      return;
    }
    await pieceLayer.animateMove(entry.move);
    busy = false;
    await afterMove(entry);
  }

  async function onSquareClicked(square: number): Promise<void> {
    if (busy || gameOver || isAI[engine.turn]) return;
    const piece = engine.getState().board[square];

    if (selected === null) {
      if (piece && piece.color === engine.turn) selectSquare(square);
      return;
    }

    if (square === selected) {
      clearSelection();
      return;
    }

    const candidates = engine.legalMovesFrom(selected).filter((m) => m.to === square);
    if (candidates.length === 0) {
      if (piece && piece.color === engine.turn) {
        selectSquare(square);
      } else {
        clearSelection();
      }
      return;
    }

    let promotion: PromotionPiece | undefined;
    if (candidates.length > 1) {
      promotion = await showPromotionModal(engine.turn);
    }

    const from = selected;
    clearSelection();
    busy = true;
    setHover(null);
    const entry = engine.move(from, square, promotion);
    if (!entry) {
      busy = false;
      return;
    }
    await pieceLayer.animateMove(entry.move);
    busy = false;
    await afterMove(entry);
  }

  let pointerDownPos: { x: number; y: number } | null = null;
  canvas.addEventListener("pointerdown", (e) => {
    pointerDownPos = { x: e.clientX, y: e.clientY };
  });
  canvas.addEventListener("pointerup", (e) => {
    if (!pointerDownPos) return;
    const dx = e.clientX - pointerDownPos.x;
    const dy = e.clientY - pointerDownPos.y;
    pointerDownPos = null;
    if (Math.hypot(dx, dy) > 6) return; // deplacement de camera (orbite), pas un tap de selection
    const intersects = scene.raycastFromPointer(e.clientX, e.clientY, scene.board.squareMeshes);
    const hit = intersects[0];
    if (!hit) return;
    const square = hit.object.userData.square as number;
    void onSquareClicked(square);
  });
  canvas.addEventListener("pointermove", (e) => {
    updateHover(e.clientX, e.clientY);
  });
  canvas.addEventListener("pointerleave", () => setHover(null));
}

/**
 * Boucle de jeu du mode "Jouer en ligne". Volontairement separee de `main()` :
 * elle reutilise les memes briques bas niveau (scene, moteur, HUD, Taverneu)
 * mais son pilotage est radicalement different (pas de chrono, pas d'IA, pas de
 * hot-seat) et le mode local, deja dense, n'a pas a en porter le risque.
 *
 * Le serveur (`dkg-multi-game-server`) est autoritaire : un coup local n'est
 * applique qu'une fois son intention acceptee, et les coups adverses arrivent
 * par `game:state` (on rejoue le delta d'historique, comme pour un bot).
 */
async function mainOnline(
  match: OnlineMatch,
  sceneFade: SceneFade,
  viewport: HTMLElement,
  uiRoot: HTMLElement,
): Promise<void> {
  const { client, selfId, myColor, names } = match;
  const opponentColor: Color = myColor === "w" ? "b" : "w";
  const settings = loadMenuSettings();

  await sceneFade.toBlack();
  document.querySelectorAll(".menu-screen").forEach((el) => el.remove());

  const scene = new SceneManager(viewport, loadGraphicsQuality());
  const canvas = scene.renderer.domElement;
  const engine = new ChessEngine();
  const pieceLayer = new PieceLayer(scene.piecesGroup, scene.tweens);
  const markers = new MoveMarkers(scene.piecesGroup);
  // En ligne, le joueur ne voit jamais que son propre camp : camera fixe de son cote,
  // exactement comme une partie locale a un seul humain.
  scene.setBaseAzimuth(myColor);

  const taverneuDirector = new TaverneuDirector(myColor);
  const captured: Record<Color, Piece[]> = { w: [], b: [] };

  let selected: number | null = null;
  let busy = true;
  let gameOver = false;
  let hoveredSquare: number | null = null;
  let showMoveHints = settings.showMoveHints;
  let taverneuCommentary = settings.taverneuCommentary;

  const hud = new Hud(names, {
    onRules: () => showRulesModal(),
    onOptions: () =>
      showOptionsModal({
        onMoveHintsChange: (enabled) => {
          showMoveHints = enabled;
        },
        onTaverneuCommentaryChange: (enabled) => {
          taverneuCommentary = enabled;
          if (!enabled) taverneuBubble.hide();
        },
        onGraphicsQualityChange: (quality) => scene.setQuality(quality),
      }),
    onResign: async () => {
      if (gameOver) return;
      const ok = await showConfirmModal("Voulez-vous vraiment abandonner la partie ?", "Abandonner");
      if (!ok || gameOver) return;
      // La fin de partie n'est pas decidee ici : elle arrivera par `game:over`.
      try {
        await client.resign();
      } catch (err) {
        hud.showAnnounce("Abandon impossible", netErrorMessage(err));
      }
    },
    onAssistant: () => {
      if (busy || gameOver || engine.turn !== myColor) return;
      const advice = getAssistantAdvice(engine.getState());
      if (advice.move) {
        selected = advice.move.from;
        markers.showSelection(advice.move.from);
        const isCapture = !!advice.move.captured || advice.move.enPassantCapture !== undefined;
        markers.showTargets([advice.move.to], isCapture ? new Set([advice.move.to]) : undefined);
      }
      taverneuBubble.say({ text: advice.text });
    },
  });
  const taverneuBubble = new TaverneuBubble(uiRoot);

  // Reprise d'une partie deja engagee : on rejoue l'historique recu sans animation
  // pour repartir exactement de la position arbitree par le serveur.
  for (const remote of client.cachedState?.history ?? []) {
    const entry = engine.move(remote.move.from, remote.move.to, remote.move.promotion);
    if (!entry) break;
    recordEntry(entry);
  }
  hud.setActive(engine.turn);

  // Toutes les applications de coups (locales comme distantes) passent par cette file
  // serielle : elle garantit qu'une animation ne se superpose jamais a une autre, et
  // que `game:over` n'arrive pas avant la fin du dernier coup. Elle reste bloquee
  // jusqu'a la fin de l'intro.
  let releaseQueue!: () => void;
  let queue: Promise<void> = new Promise<void>((resolve) => {
    releaseQueue = resolve;
  });
  function enqueue(task: () => Promise<void>): Promise<void> {
    queue = queue.then(task).catch((err: unknown) => {
      console.error("[online] traitement du coup", err);
    });
    return queue;
  }

  // Branche les evenements serveur avant l'intro : un coup adverse arrivant pendant
  // la cascade de pieces est simplement mis en file (celle-ci reste bloquee jusqu'au
  // `releaseQueue()` ci-dessous) plutot que perdu.
  registerServerEvents();

  const introPromise = pieceLayer.playIntro(engine.getState().board);
  const swoopPromise = scene.playIntroSwoop();
  await sceneFade.fromBlack();
  await Promise.all([introPromise, swoopPromise]);
  busy = false;
  if (taverneuCommentary) taverneuBubble.say(taverneuDirector.greet());
  hud.showTurnAnnounce(names[engine.turn]);
  releaseQueue();

  // --- Fonctions internes ---------------------------------------------------------

  function netErrorMessage(err: unknown): string {
    return err instanceof Error ? err.message : "Erreur reseau.";
  }

  function clearSelection(): void {
    selected = null;
    markers.hideAll();
  }

  function squareOfIntersected(object: Object3D): number | undefined {
    let node: Object3D | null = object;
    while (node) {
      if (typeof node.userData.square === "number") return node.userData.square;
      node = node.parent;
    }
    return undefined;
  }

  function setHover(square: number | null): void {
    if (square === hoveredSquare) return;
    hoveredSquare = square;
    markers.showHover(square);
    canvas.style.cursor = square !== null ? "pointer" : "";
  }

  function updateHover(clientX: number, clientY: number): void {
    if (busy || gameOver || engine.turn !== myColor) {
      setHover(null);
      return;
    }
    const intersects = scene.raycastFromPointer(clientX, clientY, [scene.piecesGroup]);
    const hit = intersects[0];
    const square = hit ? squareOfIntersected(hit.object) : undefined;
    if (square === undefined) {
      setHover(null);
      return;
    }
    const piece = engine.getState().board[square];
    setHover(piece && piece.color === engine.turn ? square : null);
  }

  function selectSquare(square: number): void {
    selected = square;
    const moves = engine.legalMovesFrom(square);
    markers.showSelection(square);
    const targets = showMoveHints ? moves.map((m) => m.to) : [];
    markers.showTargets(targets, captureSquaresOf(moves));
  }

  /** Repercute un coup deja applique au moteur sur le HUD (historique + pieces prises). */
  function recordEntry(entry: HistoryEntry): void {
    hud.addHistoryEntry(entry.stateBefore.fullmoveNumber, entry.stateBefore.turn, entry.san);
    if (entry.move.captured) {
      const by = entry.move.piece.color;
      captured[by].push(entry.move.captured);
      hud.setCaptured(by, captured[by]);
    }
  }

  function endGame(title: string, subtitle: string): void {
    if (gameOver) return;
    gameOver = true;
    clearSelection();
    setHover(null);
    // La session est terminee cote serveur : plus rien a reprendre au prochain chargement.
    clearStoredSession();
    hud.hideAnnounce();
    showEndModal(title, subtitle, () => window.location.reload());
  }

  /** Applique un coup (local ou distant) : moteur -> animation -> suites. */
  async function playMove(from: number, to: number, promotion?: PromotionPiece): Promise<void> {
    const entry = engine.move(from, to, promotion);
    if (!entry) {
      console.error("[online] coup refuse par le moteur local", { from, to, promotion });
      return;
    }
    busy = true;
    setHover(null);
    await pieceLayer.animateMove(entry.move);
    await afterMove(entry);
  }

  async function afterMove(entry: HistoryEntry): Promise<void> {
    sound.pieceMove();
    recordEntry(entry);

    const status = engine.getStatus();
    const taverneuLine = taverneuDirector.onMove(entry, status);
    if (taverneuLine && taverneuCommentary) taverneuBubble.say(taverneuLine);

    if (status === "checkmate") {
      const winner = entry.stateBefore.turn;
      endGame(winner === myColor ? "Victoire" : "Defaite", `Echec et mat : ${names[winner]} remporte la partie`);
      return;
    }
    if (status === "stalemate") {
      endGame("Pat", "Partie nulle : aucun coup legal");
      return;
    }
    if (status.startsWith("draw-")) {
      endGame("Nulle", describeDraw(status));
      return;
    }

    hud.setActive(engine.turn);
    hud.showTurnAnnounce(names[engine.turn], status === "check" ? "Echec !" : undefined);
  }

  /**
   * Envoie l'intention au serveur et n'applique le coup qu'une fois l'accuse
   * de reception positif recu (le serveur est seul juge de la legalite).
   */
  async function submitMove(from: number, to: number, promotion?: PromotionPiece): Promise<void> {
    busy = true;
    setHover(null);
    const expectedHistoryLength = engine.getHistory().length;
    try {
      await client.sendMove({ type: "move", from, to, promotion });
    } catch (err) {
      busy = false;
      hud.showAnnounce("Coup refuse", netErrorMessage(err));
      return;
    }
    // La diffusion `game:state` du serveur peut avoir applique le coup avant nous :
    // la comparaison de longueur d'historique rend l'operation idempotente.
    await enqueue(async () => {
      if (engine.getHistory().length !== expectedHistoryLength) return;
      await playMove(from, to, promotion);
    });
    busy = false;
  }

  async function onSquareClicked(square: number): Promise<void> {
    if (busy || gameOver || engine.turn !== myColor) return;
    const piece = engine.getState().board[square];

    if (selected === null) {
      if (piece && piece.color === engine.turn) selectSquare(square);
      return;
    }

    if (square === selected) {
      clearSelection();
      return;
    }

    const candidates = engine.legalMovesFrom(selected).filter((m) => m.to === square);
    if (candidates.length === 0) {
      if (piece && piece.color === engine.turn) {
        selectSquare(square);
      } else {
        clearSelection();
      }
      return;
    }

    let promotion: PromotionPiece | undefined;
    if (candidates.length > 1) {
      promotion = await showPromotionModal(engine.turn);
    }

    const from = selected;
    clearSelection();
    await submitMove(from, square, promotion);
  }

  // --- Evenements serveur ----------------------------------------------------------

  function registerServerEvents(): void {
    // Coups adverses (et rediffusion des siens) : on rejoue le delta d'historique,
    // exactement comme `maybeTriggerAiMove` rejoue le coup d'un bot en local.
    client.on("game:state", (state: ChessRemoteState) => {
      void enqueue(async () => {
        for (let i = engine.getHistory().length; i < state.history.length; i++) {
          const remote = state.history[i];
          if (!remote) break;
          await playMove(remote.move.from, remote.move.to, remote.move.promotion);
        }
        busy = false;
      });
    });

    client.on("game:over", (result: GameOverPayload) => {
      void enqueue(async () => {
        if (gameOver) return;
        if (!result.winnerIds) {
          endGame("Nulle", describeDraw(result.reason));
          return;
        }
        const won = result.winnerIds.includes(selfId);
        if (result.reason === "forfeit") {
          endGame(
            won ? "Victoire" : "Defaite",
            won ? `${names[opponentColor]} a abandonne ou quitte la partie` : "Vous avez abandonne la partie",
          );
          return;
        }
        endGame(
          won ? "Victoire" : "Defaite",
          won ? "Echec et mat : vous remportez la partie" : `Echec et mat : ${names[opponentColor]} remporte la partie`,
        );
      });
    });

    client.on("player:disconnected", (info: PlayerDisconnectedPayload) => {
      if (gameOver || info.playerId === selfId) return;
      const seconds = Math.round(info.graceMs / 1000);
      hud.showAnnounce(`${info.name} s'est deconnecte`, `Reconnexion possible pendant ${seconds} s`, true);
    });

    client.on("player:reconnected", (info: PlayerReconnectedPayload) => {
      if (info.playerId === selfId) return;
      hud.hideAnnounce();
      hud.showAnnounce(`${info.name} est de retour`);
    });

    client.onConnectionChange((online) => {
      if (gameOver) return;
      if (online) {
        hud.hideAnnounce();
        hud.showAnnounce("Connexion retablie");
      } else {
        hud.showAnnounce("Connexion perdue", "Tentative de reconnexion...", true);
      }
    });
  }

  // --- Entrees pointeur -------------------------------------------------------------

  let pointerDownPos: { x: number; y: number } | null = null;
  canvas.addEventListener("pointerdown", (e) => {
    pointerDownPos = { x: e.clientX, y: e.clientY };
  });
  canvas.addEventListener("pointerup", (e) => {
    if (!pointerDownPos) return;
    const dx = e.clientX - pointerDownPos.x;
    const dy = e.clientY - pointerDownPos.y;
    pointerDownPos = null;
    if (Math.hypot(dx, dy) > 6) return; // deplacement de camera (orbite), pas un tap de selection
    const intersects = scene.raycastFromPointer(e.clientX, e.clientY, scene.board.squareMeshes);
    const hit = intersects[0];
    if (!hit) return;
    const square = hit.object.userData.square as number;
    void onSquareClicked(square);
  });
  canvas.addEventListener("pointermove", (e) => {
    updateHover(e.clientX, e.clientY);
  });
  canvas.addEventListener("pointerleave", () => setHover(null));
}

main();
